/**
 * @author Rahul Nainala
 * @description UI View with Tree Rendering
 */
import { Vector3 } from 'three';
import { Canvas } from '@react-three/fiber';
import Points from '../re-components/Points';
import { useParams } from 'react-router-dom';
import Loader from '../re-components/Loader';
import Highlighter from 'react-highlight-words';
import InfoCard from '../re-components/InfoCard';
import { assetConfigurationData, ModelData } from '../API';
import { ModelLoader } from '../re-components/multi';
import { Environment, OrbitControls } from '@react-three/drei';
import CameraController from '../re-components/cameraController';
import { message, Tree, Input, Button, Card, Popover } from 'antd';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import {
  CameraOutlined,
  CloseCircleFilled,
  DownOutlined,
  InfoCircleOutlined,
  SearchOutlined
} from '@ant-design/icons';

const App = () => {
  let cameraRef = useRef();
  let modelGroupRef = useRef();
  let intervalRef = useRef(null);
  let [urlLink, setUrlLink] = useState(null);
  let [showUrl, setShowUrl] = useState(true);
  let [treeData, setTreeData] = useState([]);
  let twinName = '';
  let [frequency, setFrequency] = useState('');
  let [cardOpen, setCardOpen] = useState(false);
  let [annotations, setAnnotations] = useState([]);
  let [description, setDescription] = useState('');
  let [colorHelper, setColorHelper] = useState('');
  let [searchValue, setSearchValue] = useState('');
  let [expandedKeys, setExpandedKeys] = useState([]);
  // let [background, setBackground] = useState("sand");
  let background = 'snow';
  let [displayTitle, setDisplayTitle] = useState(null);
  let [visualHelpers, setVisualHelpers] = useState('');
  let [selectedMesh, setSelectedMesh] = useState(null);
  let [selectedTreeKey, setSelectedTreeKey] = useState([]);
  //   let [initialHierarchy, setInitialHierarchy] = useState([]);
  let [selectedObjectName, setSelectedObjectName] = useState(null);
  let [selectedModalConfig, setSelectedModalConfig] = useState([]);
  let [shouldAnimateCamera, setShouldAnimateCamera] = useState(false);
  let [cameraTarget, setCameraTarget] = useState(null);
  let [cameraPosition, setCameraPosition] = useState(null);
  let [initialCameraPosition, setInitialCameraPosition] = useState(null);
  let [initialCameraTarget, setInitialCameraTarget] = useState(null);

  const params = useParams();
  const moduleType = params.type;
  const moduleName = params.name;

  useEffect(() => {
    const fetchAllModels = async () => {
      try {
        const configResponse = await assetConfigurationData(moduleType, moduleName);
        const initialTreeData = configResponse?.[0]?.JSON?.parent?.hierarchy || [];
        // setInitialHierarchy(initialTreeData);
        console.log(initialTreeData);

        // Collect all trains (MODEL_LOAD nodes) from the tree
        const modelNodes = [];
        const collectModelNodes = (nodes) => {
          nodes.forEach((node) => {
            if (node.actionType === 'MODEL_LOAD') {
              modelNodes.push(node);
            }
            if (node.children) {
              collectModelNodes(node.children);
            }
          });
        };
        collectModelNodes(initialTreeData);

        // Fetch all models' annotation/config data
        const allAnnotations = [];
        let cameraPositions = [];
        let cameraTargets = [];
        let colorHelpers = [];
        let visualHelpersList = [];
        let frequencyList = [];
        // Store menu items for each train
        const menuItemsMap = {};

        for (const modelNode of modelNodes) {
          const modelResponse = await assetConfigurationData(moduleType, modelNode.key);
          const childData = modelResponse?.[0]?.JSON?.child;
          if (childData) {
            allAnnotations.push(...(childData.annotations || []));
            cameraPositions.push(childData.initalCameraPosition);
            cameraTargets.push(childData.initalCameraTarget);
            colorHelpers.push(childData.colorHelper);
            visualHelpersList.push(childData.coordinates);
            frequencyList.push(childData.frequency);

            // Build menu items for this train
            menuItemsMap[modelNode.key] = buildMenuItems(childData.annotations);
          }
        }

        // Inject menu items into every MODEL_LOAD node in the tree
        const injectAllMenus = (nodes) => {
          return nodes.map((node) => {
            let newChildren = [];
            // If this node is a MODEL_LOAD node and has menu items, add them first
            if (node.actionType === 'MODEL_LOAD' && menuItemsMap[node.key]) {
              newChildren = [...menuItemsMap[node.key]];
            }
            // If this node has original children, add them after the menu items
            if (node.children && node.children.length > 0) {
              newChildren = [...newChildren, ...injectAllMenus(node.children)];
            }
            return {
              ...node,
              children: newChildren.length > 0 ? newChildren : undefined
            };
          });
        };
        const treeWithMenus = injectAllMenus(initialTreeData);
        setTreeData(treeWithMenus);
        setExpandedKeys(getAllKeys(treeWithMenus));

        // Use the first camera position/target for initial view
        setInitialCameraPosition(cameraPositions[0]);
        setInitialCameraTarget(cameraTargets[0]);
        setCameraPosition(new Vector3(...cameraPositions[0]));
        setCameraTarget(new Vector3(...cameraTargets[0]));
        setFrequency(frequencyList[0]);
        setColorHelper(colorHelpers[0]);
        setVisualHelpers(visualHelpersList[0]);
        setAnnotations(allAnnotations);
        // setTreeData(treeWithMenus); // Set the updated tree with all menu items
        // setExpandedKeys(modelNodes.map((node) => node.key)); // Expand all trains by default
      } catch (error) {
        console.error('Operation failed:', error);
      }
    };

    fetchAllModels();
  }, [moduleType, moduleName]);

  useEffect(() => {
    if (
      Array.isArray(initialCameraPosition) &&
      initialCameraPosition.length === 3 &&
      initialCameraPosition.every((v) => typeof v === 'number') &&
      Array.isArray(initialCameraTarget) &&
      initialCameraTarget.length === 3 &&
      initialCameraTarget.every((v) => typeof v === 'number')
    ) {
      setCameraPosition(new Vector3(...initialCameraPosition));
      setCameraTarget(new Vector3(...initialCameraTarget));
    }
  }, [initialCameraPosition, initialCameraTarget]);
  useEffect(() => {
    async function fetchData() {
      if (selectedObjectName) {
        const modelConfig = annotations.find((config) => {
          if (!config.clickable) return false;
          return Object.keys(config).some((key) => {
            return (
              Array.isArray(config[key]) &&
              config[key].some((item) => item.id === selectedObjectName)
            );
          });
        });
        if (modelConfig) {
          let foundModalConfig = null;
          let categoryType = null;
          let foundItem = null;
          for (const key of Object.keys(modelConfig)) {
            if (Array.isArray(modelConfig[key])) {
              const item = modelConfig[key].find((item) => item.id === selectedObjectName);
              if (item) {
                categoryType = key;
                foundItem = item;
                if (item.showModal && item.showModal.length > 0) {
                  foundModalConfig = item.showModal[0];
                  break;
                }
              }
            }
          }

          if (foundItem) {
            setDescription(foundItem.description);
            setDisplayTitle(foundItem.parentDisplayName);
            setUrlLink(foundItem.url);
            setShowUrl(foundItem.showUrl);
          }

          if (foundModalConfig) {
            const intervalInMs = convertToMilliseconds(frequency);
            let payload = {
              Name: twinName,
              Parent_Name: modelConfig.name,
              Id: selectedObjectName,
              Type: categoryType
            };

            const fetchModelData = async () => {
              try {
                let response = await getModelData(payload);
                if (response && response.length > 0) {
                  setSelectedModalConfig(response);
                  setCardOpen(true);
                } else {
                  message.error('No data.');
                  setSelectedModalConfig([]);
                  setCardOpen(false);
                }
              } catch (error) {
                message.error('Error fetching data');
                setSelectedModalConfig([]);
                setCardOpen(false);
              }
            };

            fetchModelData();
            if (intervalInMs) {
              if (intervalRef.current) clearInterval(intervalRef.current);
              intervalRef.current = setInterval(fetchModelData, intervalInMs);
            }
          } else {
            message.error('No modal configuration found for: ' + selectedObjectName);
            setSelectedModalConfig([]);
            setCardOpen(false);
          }
        } else {
          message.error('No configuration found for the selected object');
          setSelectedModalConfig([]);
          setCardOpen(false);
        }
      } else {
        if (intervalRef.current) clearInterval(intervalRef.current);
      }
    }
    fetchData();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [selectedObjectName, frequency, annotations, twinName]);

//   const injectMenuIntoTree = (nodes, selectedKey, menuItems) => {
//     return nodes.map((node) => {
//       if (node.key === selectedKey) {
//         return {
//           ...node,
//           children: menuItems
//         };
//       }
//       if (node.children) {
//         return {
//           ...node,
//           children: injectMenuIntoTree(node.children, selectedKey, menuItems)
//         };
//       }
//       return node;
//     });
//   };

  //   const findNodeByKey = (nodes, key) => {
  //     for (const node of nodes) {
  //       if (node.key === key) return node;
  //       if (node.children) {
  //         const found = findNodeByKey(node.children, key);
  //         if (found) return found;
  //       }
  //     }
  //     return null;
  //   };

  const findParentKeys = (nodes, key, path = []) => {
    for (const node of nodes) {
      if (node.key === key) return path;
      if (node.children) {
        const result = findParentKeys(node.children, key, [...path, node.key]);
        if (result) return result;
      }
    }
    return null;
  };

  const handleCloseCard = () => {
    try {
      setCardOpen(false);
      if (modelGroupRef.current && selectedObjectName) {
        modelGroupRef.current.traverse((node) => {
          if (node.isMesh && node.userData.id === selectedObjectName) {
            if (node.userData.originalColor) {
              node.material.color.set(node.userData.originalColor);
            } else {
              node.material.color.set('#ffffff');
            }
          }
        });
      }
      if (selectedMesh) {
        selectedMesh.material.color.set('#ffffff');
      }
      setShouldAnimateCamera(false);
      setDescription('');
      setSelectedMesh(null);
      setSelectedTreeKey([]);
      setSelectedModalConfig([]);
      setSelectedObjectName(null);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } catch (e) {
      console.error('Facing error while closing the card', e);
    }
  };

  const handleAnimationComplete = () => {
    setShouldAnimateCamera(false);
    if (cameraRef.current && cameraRef.current.controls) {
      const camera = cameraRef.current.camera;
      const direction = new Vector3();
      camera.getWorldDirection(direction);
      const newTarget = camera.position.clone().add(direction.multiplyScalar(1));
      cameraRef.current.controls.target.copy(newTarget);
      cameraRef.current.controls.update();
    }
  };

  const convertToMilliseconds = (interval) => {
    try {
      let value = parseInt(interval.slice(0, -1));
      let unit = interval.slice(-1).trim();

      const conversionFactors = {
        s: 1000,
        S: 1000,
        m: 60000,
        M: 60000,
        h: 3600000,
        H: 3600000
      };

      if (unit in conversionFactors) {
        return value * conversionFactors[unit];
      } else {
        message.error("Invalid interval format. Use 's', 'm', or 'h'.");
        return null;
      }
    } catch (e) {
      console.error('Faced Issue while coverting to Ms', e);
    }
  };

  const getModelData = async (props) => {
    try {
      let data = await ModelData(props);
      return data;
    } catch (e) {
      console.error('Facing error while getting Model Data', e);
    }
  };

  const buildMenuItems = (annotations) => {
    if (!annotations || annotations.length === 0) return [];
    return annotations
      .filter((model) => model.clickable)
      .map((model) => {
        const children = [];
        for (const featureType in model) {
          if (
            featureType !== 'scale' &&
            Array.isArray(model[featureType]) &&
            model[featureType].length > 0
          ) {
            children.push({
              title: featureType,
              key: model.name + '-' + featureType,
              children: model[featureType].map((feature) => ({
                title: feature.parentDisplayName,
                key: feature.id,
                actionType: feature.actionType,
                isLeaf: true,
                data: {
                  Name: twinName,
                  Parent_Name: model.name,
                  Id: feature.id,
                  Type: featureType
                }
              }))
            });
          }
        }
        return {
          title: model.name.replace(/_/g, ' '),
          key: model.name,
          children: children.length > 0 ? children : undefined
        };
      });
  };

  const onChange = (e) => {
    const { value } = e.target;
    setSearchValue(value);
    setTreeData((prevTreeData) => markHighlightedNodes(prevTreeData, value));
    if (value) {
      setExpandedKeys(getAllKeys(treeData));
    }
  };

  const handleMenuClick = (selectedId) => {
    if (!modelGroupRef.current) return;
    modelGroupRef.current.traverse((node) => {
      if (node.isMesh && node.userData.originalColor) {
        node.material.color.set(node.userData.originalColor);
      }
    });
    modelGroupRef.current.traverse((node) => {
      if (node.isMesh && node.userData.id === selectedId) {
        if (!node.userData.originalColor) {
          node.userData.originalColor = node.material.color.clone();
        }
        node.material.color.set(colorHelper || '#ff0000');
      }
    });
    let item = null;
    for (const model of annotations) {
      for (const key in model) {
        if (Array.isArray(model[key])) {
          item = model[key].find((feature) => feature.id === selectedId);
          if (item) break;
        }
      }
      if (item) break;
    }
    if (item && item.camera) {
      setCameraPosition(new Vector3(...item.camera.position));
      setCameraTarget(new Vector3(...item.camera.target));
      setShouldAnimateCamera(true);
    }
  };

  const renderTitle = (node) => {
    const searchWords = searchValue ? [searchValue] : [];

    return (
      <span>
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={searchWords}
          autoEscape
          textToHighlight={node.title ? String(node.title) : ''}
        />
      </span>
    );
  };

  const markHighlightedNodes = (nodes, searchValue) => {
    if (!searchValue) return nodes;
    const lowerSearch = searchValue.toLowerCase();
    function mark(nodes) {
      return nodes.map((node) => {
        const keyMatch = String(node.key).toLowerCase().includes(lowerSearch);
        const titleMatch = node.title && String(node.title).toLowerCase().includes(lowerSearch);
        const highlighted = keyMatch || titleMatch;
        let children = node.children;
        if (children) {
          children = mark(children);
        }
        return {
          ...node,
          highlighted,
          children
        };
      });
    }
    return mark(nodes);
  };

  const onSelect = async (selectedKeys, { node }) => {
    setSelectedTreeKey(selectedKeys);
    if (node.actionType === 'MODEL_LOAD') {
      handleCameraReset();
      handleCloseCard();
      return;
    } else if (node.actionType === 'CLICK_ACTION' && node.data) {
      try {
        const payload = node.data;
        const selectedObjectName = payload.Id;
        const categoryType = payload.Type;
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        const modelConfig = annotations.find((config) => {
          if (!config.clickable) return false;
          return Object.keys(config).some((key) => {
            return (
              key === categoryType &&
              Array.isArray(config[key]) &&
              config[key].some((item) => item.id === selectedObjectName)
            );
          });
        });
        if (!modelConfig) {
          message.error('No configuration found for the selected object');
          setSelectedModalConfig([]);
          return;
        }
        const itemsArray = modelConfig[categoryType] || [];
        const item = itemsArray.find((i) => i.id === selectedObjectName);
        let foundModalConfig = item?.showModal?.[0];
        if (item) {
          setDescription(item.description);
          setDisplayTitle(item.parentDisplayName);
          setUrlLink(item.url);
          setShowUrl(item.showUrl);
          setSelectedObjectName(selectedObjectName);
          handleMenuClick(selectedObjectName);
        }
        if (!foundModalConfig) {
          message.error(`No modal configuration found for: ${selectedObjectName}`);
          setSelectedModalConfig([]);
        }
      } catch (error) {
        message.error('Selection processing failed');
        console.error('Selection error:', error);
      }
    }
  };

  const getAllKeys = (treeData) => {
    let keys = [];
    treeData.forEach((node) => {
      keys.push(node.key);
      if (node.children) {
        keys = keys.concat(getAllKeys(node.children));
      }
    });
    return keys;
  };

//   setExpandedKeys(getAllKeys(treeWithMenus));

  const handleCameraReset = () => {
    handleCloseCard();
    setCameraTarget(
      new Vector3(initialCameraTarget[0], initialCameraTarget[1], initialCameraTarget[2])
    );
    setCameraPosition(
      new Vector3(initialCameraPosition[0], initialCameraPosition[1], initialCameraPosition[2])
    );
    if (cameraRef.current) {
      cameraRef.current.reset();
    }
  };

  const navigationInfo = (
    <Card
      title="3D Navigation Controls"
      size="default"
      style={{ width: 300 }}
      variant={'borderless'}>
      <ul style={{ paddingLeft: 18, margin: 0 }}>
        <li>
          <b>Rotate:</b> Left Mouse Button
        </li>
        <li>
          <b>Pan:</b> Right Mouse Button or Ctrl + Left Mouse
        </li>
        <li>
          <b>Zoom:</b> Mouse Wheel
        </li>
        <li>
          <b>Reset:</b> Click the <CameraOutlined /> button
        </li>
      </ul>
    </Card>
  );

  return (
    <div>
      {cardOpen && selectedObjectName && selectedModalConfig && (
        <InfoCard
          cardOpen={cardOpen}
          selectedObjectName={selectedObjectName}
          selectedModalConfig={selectedModalConfig}
          displayTitle={displayTitle}
          description={description}
          urlLink={urlLink}
          showUrl={showUrl}
          onClose={handleCloseCard}
          titleRender={renderTitle}
        />
      )}
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div style={{ height: '92vh', width: '98vw' }}>
          <div
            style={{
              position: 'absolute',
              top: 75,
              left: 10,
              width: '22vw',
              zIndex: 20,
              padding: 8,
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
            <Input
              style={{ width: '100%', margin: '5px 0' }}
              placeholder={'Search here'}
              prefix={<SearchOutlined />}
              onChange={onChange}
              value={searchValue}
              suffix={searchValue ? <CloseCircleFilled onClick={() => setSearchValue('')} /> : null}
            />
            <>
              <div style={{ flex: 1, margin: '5px', padding: '15px' }}>
                <Tree
                  style={{ height: '90vh', overflowY: 'auto' }}
                  treeData={treeData}
                  expandedKeys={
                    searchValue && searchValue.length > 0 ? getAllKeys(treeData) : expandedKeys
                  }
                  onExpand={setExpandedKeys}
                  showLine
                  onSelect={onSelect}
                  selectedKeys={selectedTreeKey}
                  switcherIcon={<DownOutlined />}
                  titleRender={renderTitle}
                />
              </div>
            </>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '10px',
              position: 'absolute',
              top: 90,
              right: 20,
              zIndex: 10,
              cursor: 'pointer'
            }}>
            <Button onClick={handleCameraReset}>
              <CameraOutlined /> Reset
            </Button>
            <Popover
              content={navigationInfo}
              title={null}
              trigger="hover"
              placement="bottomRight"
              overlayStyle={{ padding: 0 }}>
              <Button>
                <InfoCircleOutlined />
              </Button>
            </Popover>
          </div>
          <Canvas
            camera={{
              position: cameraPosition
                ? [cameraPosition.x, cameraPosition.y, cameraPosition.z]
                : [13, 13, 13]
            }}>
            <Suspense fallback={<Loader />}>
              <ambientLight intensity={0.4} />
              <Environment files={`http://20.198.16.173/Models/hdr/${background}.hdr`} />
              <OrbitControls
                ref={cameraRef}
                target={cameraTarget}
                enableZoom={true}
                enableRotate={true}
                enablePan={true}
                enableDamping={true}
                dampingFactor={0.05}
                minDistance={1}
                maxDistance={100}
                minPolarAngle={0}
                maxPolarAngle={Math.PI / 2}
                makeDefault
              />
              <CameraController
                lerpFactor={0.05}
                cameraTarget={cameraTarget}
                animate={shouldAnimateCamera}
                cameraPosition={cameraPosition}
                onAnimationComplete={handleAnimationComplete}
              />
              <ModelLoader
                selectedObjectNameGlobal={selectedObjectName}
                colorHelper={colorHelper}
                config={annotations}
                groupRef={modelGroupRef}
                setShouldAnimateCamera={setShouldAnimateCamera}
                selectedMesh={selectedMesh}
                setSelectedMesh={(mesh) => {
                  setSelectedMesh(mesh);
                  setSelectedObjectName(mesh ? mesh.name : null);
                  if (mesh) {
                    const parentKeys = findParentKeys(treeData, mesh.name) || [];
                    setExpandedKeys((prev) => Array.from(new Set([...prev, ...parentKeys])));
                    setSelectedTreeKey([mesh.name]);
                  } else {
                    setSelectedTreeKey([]);
                  }
                }}
              />
              {visualHelpers === 'true' ? (
                <>
                  <axesHelper args={[5]} />
                  <Points />
                </>
              ) : null}
            </Suspense>
          </Canvas>
        </div>
      </div>
    </div>
  );
};

export default App;
